import React, { useState, useEffect } from 'react';
import {
  Button,
  Mentions,
  Typography
} from 'antd';
import dayjs from 'dayjs';
import { OutputData } from '@editorjs/editorjs';
import { KanbanTaskComment } from '../../../../stores/KanbanTaskStores';
import ReadOnlyEditor from '../../ReadOnlyRichTextEditor';
import RichTextEditor from '../../RichTextEditor';
import { useStore } from '../../../../stores/root-store-context';

const { Text } = Typography;

// Helper function to check if a string is valid JSON
const isJsonString = (str: string): boolean => {
  if (!str) return false;
  try {
    const parsed = JSON.parse(str);
    return typeof parsed === 'object';
  } catch (e) {
    return false;
  }
};

// Enhanced safe parse function for editor data with bold blue mention tag formatting
const safeParseEditorData = (raw: string): OutputData => {
  if (!raw) return { blocks: [] };
  const formatMentions = (text: string) =>
    text ? text.replace(
      /@\[(.*?)\]\(user:\d+\)/g,
      '<b style="color: #1890ff; background-color: #e6f7ff; padding: 2px 6px; border-radius: 4px; font-weight: 600; display: inline-block; margin: 0 2px;">@$1</b>'
    ) : text;

  try {
    const data = JSON.parse(raw) as OutputData;
    if (data && data.blocks && Array.isArray(data.blocks)) {
      data.blocks.forEach((block: any) => {
        if (block.data && typeof block.data.text === 'string') {
          block.data.text = formatMentions(block.data.text);
        }
      });
    }
    return data;
  } catch {
    return {
      blocks: [
        {
          type: "paragraph",
          data: {
            text: formatMentions(raw)
          }
        }
      ]
    };
  }
};

// Component to display an individual comment
const CommentItem: React.FC<{
  comment: KanbanTaskComment;
  t: (key: string) => string;
}> = ({ comment, t }) => {
  // Determine if the comment content is rich text (JSON) or plain text
  const isRichText = isJsonString(comment.content);

  // Parse the comment content appropriately
  const commentData = isRichText
    ? safeParseEditorData(comment.content)
    : safeParseEditorData(JSON.stringify({
      blocks: [{ type: "paragraph", data: { text: comment.content } }]
    }));

  return (
    <div className="comment-item">
      <div className="comment-header">
        <div className="comment-author-info">
          <span className="comment-author">
            {comment.author ? `${comment.author.firstName} ${comment.author.lastName}` : 'Unknown'}
          </span>
        </div>
        <div className="comment-metadata">
          <span className="comment-date">
            {dayjs(comment.createdDate).format('LLL')}
          </span>
        </div>
      </div>

      <div className="comment-content">
        <div className="comment-display">
          <ReadOnlyEditor data={commentData} />
        </div>
      </div>
    </div>
  );
};

// Comment input component using RichTextEditor (EditorJS with full toolbar & @mentions)
const CommentInput: React.FC<{
  onAddComment: (comment: string) => Promise<boolean>;
  selectedTaskId?: string;
  t: (key: string) => string;
}> = React.memo(({ onAddComment, selectedTaskId, t }) => {
  const initialData: OutputData = { blocks: [] };
  const [editorData, setEditorData] = useState<OutputData>(initialData);
  const [richTextEditorKey, setRichTextEditorKey] = useState(Date.now());

  // Reset editor when task changes
  useEffect(() => {
    setEditorData(initialData);
  }, [selectedTaskId]);

  const handleEditorChange = (data: OutputData) => {
    setEditorData(data);
  };

  const handleAddCommentClick = async () => {
    const hasContent = editorData.blocks && editorData.blocks.length > 0 &&
      editorData.blocks.some((block: any) =>
        block.data && block.data.text && block.data.text.trim());

    if (hasContent) {
      const success = await onAddComment(JSON.stringify(editorData));
      if (success) {
        setEditorData(initialData);
        setRichTextEditorKey(Date.now());
      }
    }
  };

  return (
    <div className="comment-input-container">
      <div className="rich-comment-editor">
        <RichTextEditor
          key={richTextEditorKey}
          initialData={editorData}
          onChange={handleEditorChange}
        />
      </div>

      <div className="comment-actions">
        <Button
          type="primary"
          onClick={handleAddCommentClick}
          className="comment-button hover-effect"
        >
          {t('AddComment')}
        </Button>
      </div>
    </div>
  );
});

// Comments container component
const RichTextComments: React.FC<{
  comments: KanbanTaskComment[];
  onAddComment: (comment: string) => Promise<boolean>;
  selectedTaskId?: string;
  t: (key: string) => string;
  isReadOnly: boolean;
}> = ({ comments, onAddComment, selectedTaskId, t, isReadOnly }) => {
  return (
    <div className="comments-container">
      <div className="comments-list">
        {comments.length > 0 ? (
          comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              t={t}
            />
          ))
        ) : (
          <p className="no-comments">{t('NoCommentsYet')}</p>
        )}
      </div>

      { !isReadOnly && <CommentInput
        key={selectedTaskId}
        onAddComment={onAddComment}
        selectedTaskId={selectedTaskId}
        t={t}
      />}
    </div>
  );
};

export default RichTextComments;